import { finalizeEvent, generateSecretKey, getPublicKey, verifyEvent } from "nostr-tools/pure";
import { makeAuthEvent } from "nostr-tools/nip42";
import { decode, npubEncode } from "nostr-tools/nip19";
import { encrypt as encryptNip49, decrypt as decryptNip49 } from "nostr-tools/nip49";

const DB_NAME = "buzz-community-web";
const STORE = "identity";
const IDENTITY_KEY = "primary";

const state = { secretKey: null, pubkey: null, npub: null, socket: null, authenticated: false, channels: new Map(), events: [], pendingAuthId: null };
const $ = (id) => document.getElementById(id);

function show(id, visible = true) { $(id)?.classList.toggle("hidden", !visible); }
function setText(id, value) { if ($(id)) $(id).textContent = value; }
function error(message = "") { setText("identity-error", message); show("identity-error", Boolean(message)); }
function log(message, kind = "") { const line = document.createElement("div"); line.className = `log-line ${kind}`; line.textContent = `${new Date().toLocaleTimeString()}  ${message}`; $("protocol-log").prepend(line); }

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
async function vaultGet() { const db = await openDb(); return new Promise((resolve, reject) => { const tx = db.transaction(STORE); const req = tx.objectStore(STORE).get(IDENTITY_KEY); req.onsuccess = () => resolve(req.result ?? null); req.onerror = () => reject(req.error); }); }
async function vaultSet(value) { const db = await openDb(); return new Promise((resolve, reject) => { const tx = db.transaction(STORE, "readwrite"); tx.objectStore(STORE).put(value, IDENTITY_KEY); tx.oncomplete = resolve; tx.onerror = () => reject(tx.error); }); }
async function vaultDelete() { const db = await openDb(); return new Promise((resolve, reject) => { const tx = db.transaction(STORE, "readwrite"); tx.objectStore(STORE).delete(IDENTITY_KEY); tx.oncomplete = resolve; tx.onerror = () => reject(tx.error); }); }

function setUnlocked(secretKey) {
  state.secretKey = secretKey;
  state.pubkey = getPublicKey(secretKey);
  state.npub = npubEncode(state.pubkey);
  setText("npub-value", state.npub);
  setText("hex-value", state.pubkey);
  setText("identity-badge", "Unlocked");
  show("identity-empty", false); show("unlock-form", false); show("identity-ready", true);
  $("connect-button").disabled = false;
  setText("connection-note", "Ready to authenticate with the relay.");
}

function lockIdentity() {
  disconnect();
  state.secretKey = null; state.pubkey = null; state.npub = null;
  show("identity-ready", false); show("unlock-form", true);
  setText("identity-badge", "Locked");
  $("connect-button").disabled = true;
  setText("connection-note", "Unlock an identity to connect.");
}

async function saveIdentity(secretKey, password) {
  const encrypted = await encryptNip49(secretKey, password);
  await vaultSet({ encrypted, createdAt: new Date().toISOString() });
  setUnlocked(secretKey);
}

function parsePrivateKey(raw) {
  const value = raw.trim();
  if (/^[0-9a-f]{64}$/i.test(value)) return Uint8Array.from(value.match(/.{2}/g).map((byte) => Number.parseInt(byte, 16)));
  if (value.startsWith("nsec1")) { const decoded = decode(value); if (decoded.type !== "nsec" || !(decoded.data instanceof Uint8Array)) throw new Error("Invalid nsec key."); return decoded.data; }
  throw new Error("Enter an nsec1 key or exactly 64 hexadecimal characters.");
}

function updateConnection(status, online = false) {
  setText("connection-badge", status);
  setText("relay-status", online ? "Connected" : status);
  $("relay-status").classList.toggle("online", online);
}

function disconnect() {
  if (state.socket) { try { state.socket.close(); } catch {} }
  state.socket = null; state.authenticated = false; state.pendingAuthId = null;
  updateConnection("Offline", false); show("community-section", false);
}

function sendReq(id, filter) { state.socket?.send(JSON.stringify(["REQ", id, filter])); }
function beginQueries() {
  sendReq("memberships", { kinds: [39002], "#p": [state.pubkey], limit: 1000 });
  sendReq("profiles", { kinds: [0], authors: [state.pubkey], limit: 1 });
  log("Authenticated. Loading community membership.");
  show("community-section", true);
}

function tag(event, name) { return event.tags?.find((item) => item[0] === name)?.[1] ?? null; }
function channelName(event) { return tag(event, "name") || (() => { try { return JSON.parse(event.content || "{}").name; } catch { return null; } })() || `Channel ${tag(event, "d")?.slice(0, 8) ?? "unknown"}`; }

function handleEvent(subscription, event) {
  if (!event || !verifyEvent(event)) { log("Ignored an event with an invalid signature.", "error"); return; }
  if (subscription === "memberships") {
    const ids = [...new Set(event.tags.filter((item) => item[0] === "d" && item[1]).map((item) => item[1]))];
    if (ids.length) sendReq("channel-meta", { kinds: [39000], "#d": ids, limit: ids.length });
    return;
  }
  if (subscription === "channel-meta") {
    const id = tag(event, "d"); if (!id) return;
    state.channels.set(id, { id, name: channelName(event), event }); renderChannels();
    return;
  }
  if (subscription.startsWith("messages:")) {
    if (!state.events.some((existing) => existing.id === event.id)) state.events.push(event);
    state.events.sort((a, b) => b.created_at - a.created_at); renderEvents();
  }
}

function renderChannels() {
  const root = $("channels"); root.replaceChildren();
  if (!state.channels.size) { root.innerHTML = '<p class="connection-note">No channel memberships found.</p>'; return; }
  [...state.channels.values()].sort((a,b) => a.name.localeCompare(b.name)).forEach((channel) => {
    const button = document.createElement("button"); button.className = "channel-button"; button.textContent = `# ${channel.name}`;
    button.addEventListener("click", () => selectChannel(channel, button)); root.append(button);
  });
}

function selectChannel(channel, button) {
  document.querySelectorAll(".channel-button").forEach((item) => item.classList.remove("active")); button.classList.add("active");
  setText("timeline-title", `# ${channel.name}`); state.events = []; renderEvents();
  sendReq(`messages:${channel.id}`, { kinds: [9, 40002, 40008, 40099], "#h": [channel.id], limit: 100 });
}

function renderEvents() {
  const root = $("events"); root.replaceChildren(); setText("event-count", `${state.events.length} event${state.events.length === 1 ? "" : "s"}`);
  if (!state.events.length) { root.innerHTML = '<div class="empty-state"><p>No messages have arrived for this channel.</p></div>'; return; }
  state.events.forEach((event) => {
    const row = document.createElement("article"); row.className = "event";
    const meta = document.createElement("div"); meta.className = "event-meta";
    const author = document.createElement("span"); author.textContent = `${event.pubkey.slice(0, 12)}…`;
    const time = document.createElement("time"); time.textContent = new Date(event.created_at * 1000).toLocaleString();
    const content = document.createElement("p"); content.textContent = event.content || `[Buzz event kind ${event.kind}]`;
    meta.append(author, time); row.append(meta, content); root.append(row);
  });
}

async function connect() {
  if (!state.secretKey) return;
  disconnect(); const relayUrl = $("relay-url").value.trim();
  if (!relayUrl.startsWith("wss://")) { log("A secure wss:// relay URL is required.", "error"); return; }
  updateConnection("Connecting", false); log(`Opening ${relayUrl}`);
  const ws = new WebSocket(relayUrl); state.socket = ws;
  ws.addEventListener("open", () => { updateConnection("Authenticating", false); log("Secure socket opened; waiting for relay challenge."); });
  ws.addEventListener("message", (message) => {
    let data; try { data = JSON.parse(String(message.data)); } catch { return; }
    if (!Array.isArray(data)) return;
    const [type] = data;
    if (type === "AUTH" && typeof data[1] === "string") {
      const signed = finalizeEvent(makeAuthEvent(relayUrl, data[1]), state.secretKey); state.pendingAuthId = signed.id;
      ws.send(JSON.stringify(["AUTH", signed])); log("Signed the relay challenge locally."); return;
    }
    if (type === "OK" && data[1] === state.pendingAuthId) {
      if (data[2] === true) { state.authenticated = true; updateConnection("Online", true); beginQueries(); }
      else { updateConnection("Denied", false); log(data[3] || "Relay authentication denied.", "error"); }
      return;
    }
    if (type === "EVENT" && typeof data[1] === "string") handleEvent(data[1], data[2]);
    else if (type === "CLOSED") log(`Subscription closed: ${data[2] || "no reason"}`, "error");
    else if (type === "NOTICE") log(`Relay notice: ${data[1]}`, "error");
  });
  ws.addEventListener("error", () => { updateConnection("Error", false); log("Could not reach the relay.", "error"); });
  ws.addEventListener("close", () => { if (state.socket === ws) { state.authenticated = false; updateConnection("Offline", false); log("Relay connection closed."); } });
}

document.querySelectorAll(".tab").forEach((button) => button.addEventListener("click", () => {
  document.querySelectorAll(".tab").forEach((item) => item.classList.toggle("active", item === button));
  show("create-form", button.dataset.tab === "create"); show("import-form", button.dataset.tab === "import"); error();
}));

$("create-form").addEventListener("submit", async (event) => { event.preventDefault(); error(); const password = $("create-passphrase").value; if (password !== $("create-confirm").value) return error("Passphrases do not match."); try { await saveIdentity(generateSecretKey(), password); event.target.reset(); } catch (cause) { error(cause instanceof Error ? cause.message : "Could not create identity."); } });
$("import-form").addEventListener("submit", async (event) => { event.preventDefault(); error(); try { await saveIdentity(parsePrivateKey($("import-key").value), $("import-passphrase").value); event.target.reset(); } catch (cause) { error(cause instanceof Error ? cause.message : "Could not import identity."); } });
$("unlock-form").addEventListener("submit", async (event) => { event.preventDefault(); error(); try { const record = await vaultGet(); setUnlocked(await decryptNip49(record.encrypted, $("unlock-passphrase").value)); event.target.reset(); } catch { error("Wrong passphrase or damaged browser identity."); } });
$("lock-button").addEventListener("click", lockIdentity);
$("forget-button").addEventListener("click", async () => { if (!confirm("Forget the encrypted identity stored by this browser? This cannot be undone without a backup.")) return; disconnect(); await vaultDelete(); location.reload(); });
$("connect-button").addEventListener("click", connect);
$("refresh-button").addEventListener("click", () => { state.channels.clear(); state.events = []; renderChannels(); renderEvents(); beginQueries(); });
document.querySelectorAll("[data-copy]").forEach((button) => button.addEventListener("click", async () => { await navigator.clipboard.writeText($(button.dataset.copy).textContent); const before = button.textContent; button.textContent = "Copied"; setTimeout(() => button.textContent = before, 1200); }));

(async function init() {
  try { const config = await fetch("/api/web/config.php", { cache: "no-store" }).then((response) => response.json()); if (config.relayUrl) $("relay-url").value = config.relayUrl; } catch {}
  const record = await vaultGet(); show("identity-empty", !record); show("unlock-form", Boolean(record));
  renderEvents(); renderChannels();
})();
